/* Dýravinir UX-140: progressive enhancements with no external dependency. */
(() => {
  'use strict';

  const clean = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const lower = (value) => clean(value).toLocaleLowerCase('is');
  const weightPattern = /\b\d+(?:[.,]\d+)?\s*(?:kg|g|gr\.?)\b/i;

  function analyticsAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return Boolean(privacy && privacy.analyticsProcessingAllowed());
    } catch (_) {
      return false;
    }
  }

  function track(event, payload = {}) {
    if (!analyticsAllowed()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  }

  function improvePackageChoice(root = document) {
    root.querySelectorAll('[data-dv-next20-sizes]').forEach((container) => {
      const title = container.dataset.productTitle || '';
      const currentWeight = title.match(weightPattern)?.[0] || '';
      const food = /fóður|matur|nammi/i.test(title) || /wolfsblut|taste of the wild|diamond|vetline/i.test(container.dataset.productVendor || '');
      const label = container.querySelector('.dv-next20-sizes__label');
      if (label) label.textContent = food ? 'Veldu pakkningastærð' : 'Veldu stærð';
      container.querySelectorAll('a').forEach((link) => {
        const weight = clean(link.textContent).match(weightPattern)?.[0];
        if (weight) link.textContent = weight;
        if (link.dataset.dv140Tracked !== 'true') {
          link.dataset.dv140Tracked = 'true';
          link.addEventListener('click', () => track('dv_pdp_package_choice', { destination: link.pathname }));
        }
      });
      container.querySelectorAll('.is-current').forEach((current) => {
        const weight = clean(current.textContent).match(weightPattern)?.[0];
        if (weight) current.textContent = weight;
      });
      const links = container.querySelector('.dv-next20-sizes__links');
      if (links && currentWeight && !links.querySelector('.is-current')) {
        const current = document.createElement('span');
        current.className = 'is-current';
        current.textContent = currentWeight;
        current.setAttribute('aria-current', 'page');
        current.setAttribute('title', 'Valin pakkning');
        links.prepend(current);
      }
      if (links?.children.length) container.hidden = false;
    });
  }

  function syncPurchaseConfidence(root = document) {
    const unavailable = root.querySelector('[data-dv140-unavailable]') || document.querySelector('[data-dv140-unavailable]');
    const submit = document.querySelector('.product-form__submit, button[name="add"]');
    if (!unavailable || !submit) return;
    const sync = () => { unavailable.hidden = !(submit.disabled || submit.getAttribute('aria-disabled') === 'true'); };
    sync();
    if (unavailable.dataset.dv140Watching === 'true') return;
    unavailable.dataset.dv140Watching = 'true';
    new MutationObserver(sync).observe(submit, { attributes: true, childList: true, subtree: true });
  }

  function buildDecisionNavigation(root = document) {
    const nav = root.querySelector('[data-dv140-decision-nav]') || document.querySelector('[data-dv140-decision-nav]');
    if (!nav || nav.dataset.ready === 'true') return;
    const productInfo = nav.closest('.product__info-container') || nav.closest('.product__info-wrapper') || document.querySelector('.product__info-container');
    const targets = Array.from(productInfo?.querySelectorAll('.product__accordion summary, .accordion summary') || []);
    const wanted = [
      ['um vöruna', 'Um vöruna'], ['innihald', 'Innihald'], ['samsetning', 'Samsetning'],
      ['næring', 'Næringargildi'], ['fóðrun', 'Fóðrun'], ['stærð', 'Stærð og snið']
    ];
    const links = nav.querySelector('.dv140-decision-nav__links');
    const usedTargets = new Set();
    wanted.forEach(([needle, label]) => {
      const target = targets.find((item) => lower(item.textContent).startsWith(needle));
      const details = target?.closest('details');
      if (!target || !links || usedTargets.has(details || target) || links.querySelector(`[data-kind="${needle}"]`)) return;
      const id = details?.id || target.id || `dv140-${needle.replace(/\s+/g, '-')}`;
      if (!details?.id && !target.id) target.id = id;
      usedTargets.add(details || target);
      const link = document.createElement('a');
      link.href = `#${id}`;
      link.dataset.kind = needle;
      link.textContent = label;
      link.addEventListener('click', () => {
        if (details) details.open = true;
        track('dv_pdp_decision_link', { kind: needle });
      });
      links.appendChild(link);
    });
    nav.hidden = !links?.children.length;
    nav.dataset.ready = 'true';
  }

  function localizeRatings(root = document) {
    root.querySelectorAll('.rating[aria-label], [role="img"][aria-label*="out of" i]').forEach((rating) => {
      const label = rating.getAttribute('aria-label') || '';
      const match = label.match(/([0-9.,]+)\s+out of\s+([0-9.,]+)\s+stars?/i);
      if (!match) return;
      rating.setAttribute('aria-label', `${match[1].replace('.', ',')} af ${match[2].replace('.', ',')} stjörnum`);
    });
  }

  function hidePrimarySubscriptionNavigation(root = document) {
    root.querySelectorAll('.header__inline-menu a, .header__menu-item, header-drawer a').forEach((link) => {
      if (lower(link.textContent) !== 'fóður í áskrift') return;
      const item = link.closest('li') || link;
      item.hidden = true;
      item.dataset.dv140RetailNav = 'hidden';
    });
  }

  function improveSearch(root = document) {
    if (!document.body.classList.contains('template-search')) return;
    const query = lower(new URLSearchParams(location.search).get('q'));
    const grid = root.querySelector('#product-grid .product-grid') || document.querySelector('#product-grid .product-grid');
    if (!grid) return;
    const items = Array.from(grid.querySelectorAll(':scope > li.grid__item'));
    let products = 0;
    let content = 0;
    items.forEach((item) => {
      const productLink = item.querySelector('.card__heading a[href*="/products/"], a.full-unstyled-link[href*="/products/"]');
      const articleLink = item.querySelector('a[href*="/blogs/"], .article-card-wrapper a');
      const type = productLink ? 'product' : (articleLink ? 'article' : 'page');
      item.dataset.dv140ResultType = type;
      if (type === 'product' && query === 'lambakjöt' && !lower(productLink.textContent).includes('lamb')) {
        item.hidden = true;
        item.dataset.dv140Filtered = 'intent-mismatch';
        return;
      }
      if (type === 'product') products += 1;
      else content += 1;
      productLink?.addEventListener('click', () => track('dv_search_result_click', { result_type: 'product', query }));
      articleLink?.addEventListener('click', () => track('dv_search_result_click', { result_type: type, query }));
    });
    const signature = `${query}:${products}:${content}:${items.filter((item) => item.hidden).length}`;
    if (grid.dataset.dv140Signature === signature) return;
    grid.dataset.dv140Signature = signature;
    grid.querySelectorAll('.dv140-result-heading').forEach((heading) => heading.remove());
    if (products) {
      const heading = document.createElement('li');
      heading.className = 'dv140-result-heading dv140-result-heading--products';
      heading.innerHTML = `<h2>Vörur <small>(${products})</small></h2>`;
      grid.prepend(heading);
    }
    if (content) {
      const heading = document.createElement('li');
      heading.className = 'dv140-result-heading dv140-result-heading--content';
      heading.innerHTML = `<h2>Greinar og leiðbeiningar <small>(${content})</small></h2>`;
      grid.appendChild(heading);
    }
  }

  const foodFacetPriority = ['tegund fóðurs', 'til á lager', 'aldur hunds', 'aldur kattar', 'próteingjafi', 'án þessara innihaldsefna', 'pakkningastærð', 'vörumerki', 'verð'];
  const gearFacetPriority = ['til á lager', 'stærð', 'litur', 'tegund', 'vörumerki', 'verð'];
  function contextualFacets(root = document) {
    if (!document.body.classList.contains('template-collection')) return;
    const food = /fóður|dry-food|blautmatur|nammi/i.test(`${location.pathname} ${document.querySelector('h1')?.textContent || ''}`);
    const priority = food ? foodFacetPriority : gearFacetPriority;
    root.querySelectorAll('#FacetsWrapperDesktop, .mobile-facets__main').forEach((container) => {
      const mode = food ? 'food' : 'gear';
      if (container.dataset.dv140FacetMode === mode) return;
      const facets = Array.from(container.querySelectorAll(':scope > .facets__disclosure-vertical, :scope > .mobile-facets__details'));
      facets.sort((a, b) => {
        const name = (node) => lower(node.querySelector(':scope > summary')?.textContent).replace(/\s*\(.*/, '');
        const index = (node) => { const found = priority.findIndex((label) => name(node).includes(label)); return found < 0 ? 99 : found; };
        return index(a) - index(b);
      }).forEach((facet) => container.appendChild(facet));
      container.dataset.dv140FacetMode = mode;
    });
  }

  function productCards(root = document) {
    return Array.from(root.querySelectorAll('.product-card-wrapper')).filter((card) => card.querySelector('a[href*="/products/"]'));
  }

  const compareStorageKey = 'dyravinir:ux160:compare';
  let compared = [];
  try {
    const saved = JSON.parse(localStorage.getItem(compareStorageKey) || '[]');
    compared = Array.isArray(saved)
      ? saved.filter((item) => item && /^\/products\/[a-z0-9-]+$/.test(item.url || '')).slice(0, 3)
      : [];
  } catch (_) {}
  function saveCompared() {
    try { localStorage.setItem(compareStorageKey, JSON.stringify(compared)); } catch (_) {}
  }
  function renderCompareTray() {
    let tray = document.querySelector('.dv140-compare-tray');
    if (!tray) {
      tray = document.createElement('aside');
      tray.className = 'dv140-compare-tray';
      tray.setAttribute('aria-label', 'Vörusamanburður');
      tray.innerHTML = '<div class="dv140-compare-tray__header"><strong>Bera saman</strong><button class="dv140-compare-tray__close" type="button" aria-label="Loka samanburði">×</button></div><ul></ul>';
      tray.querySelector('.dv140-compare-tray__close').addEventListener('click', () => { compared = []; saveCompared(); renderCompareTray(); });
      document.body.appendChild(tray);
    }
    tray.hidden = compared.length === 0;
    const list = tray.querySelector('ul');
    list.innerHTML = '';
    compared.forEach((item) => {
      const row = document.createElement('li');
      const summary = document.createElement('div');
      const link = document.createElement('a');
      link.href = item.url;
      link.textContent = item.title;
      const price = document.createElement('div');
      price.textContent = item.price;
      const facts = document.createElement('small');
      facts.textContent = item.facts;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.remove = item.url;
      remove.textContent = 'Fjarlægja';
      summary.append(link, price, facts);
      row.append(summary, remove);
      list.appendChild(row);
    });
    tray.querySelectorAll('[data-remove]').forEach((button) => button.addEventListener('click', () => {
      compared = compared.filter((item) => item.url !== button.dataset.remove);
      saveCompared();
      document.querySelectorAll('.dv140-compare-button').forEach((control) => {
        if (control.dataset.url === button.dataset.remove) control.setAttribute('aria-pressed', 'false');
      });
      renderCompareTray();
    }));
  }

  function addCompareControls(root = document) {
    if (!document.body.classList.contains('template-collection') && !document.body.classList.contains('template-search')) return;
    productCards(root).forEach((card) => {
      if (card.querySelector('.dv140-compare-button')) return;
      const link = card.querySelector('.card__heading a[href*="/products/"]');
      const price = clean(card.querySelector('.price')?.textContent);
      const facts = clean(card.querySelector('.dv-next20-facts')?.textContent);
      if (!link || !/fóður|matur|wolfsblut|taste of the wild|diamond/i.test(`${link.textContent} ${facts}`)) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dv140-compare-button';
      button.textContent = 'Bera saman';
      button.dataset.url = link.pathname;
      button.setAttribute('aria-pressed', String(compared.some((item) => item.url === link.pathname)));
      if (button.getAttribute('aria-pressed') === 'true') button.textContent = 'Valið til samanburðar';
      button.addEventListener('click', () => {
        const selected = button.getAttribute('aria-pressed') === 'true';
        if (selected) compared = compared.filter((item) => item.url !== link.pathname);
        else if (compared.length < 3) compared.push({ url: link.pathname, title: clean(link.textContent), price, facts });
        saveCompared();
        button.setAttribute('aria-pressed', String(!selected && compared.some((item) => item.url === link.pathname)));
        button.textContent = button.getAttribute('aria-pressed') === 'true' ? 'Valið til samanburðar' : 'Bera saman';
        track('dv_product_compare', { action: selected ? 'remove' : 'add', count: compared.length });
        renderCompareTray();
      });
      (card.querySelector('.quick-add') || card.querySelector('.card__content:last-child') || card).appendChild(button);
    });
    if (compared.length) renderCompareTray();
  }

  function hardenStickyActions(root = document) {
    root.querySelectorAll('.dv-sticky-atc__button, .dv-sticky-cart__button, .cart__checkout-button').forEach((button) => {
      if (button.dataset.dv140Tracked === 'true') return;
      button.dataset.dv140Tracked = 'true';
      button.addEventListener('click', () => track(button.classList.contains('dv-sticky-atc__button') ? 'dv_sticky_add_to_cart' : 'dv_cart_checkout_click', { sticky: button.classList.contains('dv-sticky-atc__button') || button.classList.contains('dv-sticky-cart__button') }));
    });
  }

  function enhance(root = document) {
    improvePackageChoice(root);
    syncPurchaseConfidence(root);
    buildDecisionNavigation(root);
    localizeRatings(root);
    hidePrimarySubscriptionNavigation(root);
    improveSearch(root);
    contextualFacets(root);
    addCompareControls(root);
    hardenStickyActions(root);
    document.body.dataset.dvUx140 = 'ready';
  }

  const run = () => enhance(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', (event) => enhance(event.target));
  const main = document.querySelector('#MainContent');
  if (main && 'MutationObserver' in window) {
    let scheduled = false;
    new MutationObserver((records) => {
      if (scheduled || !records.some((record) => record.addedNodes.length)) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; enhance(main); });
    }).observe(main, { childList: true, subtree: true });
  }
})();
